import {RuntimeAdapter} from '@assistant-ui/core/react';
import {AuiProvider, useAui} from '@assistant-ui/store';
import {useLLMProxyClient} from '@wandb/weave/lib/LlmProxy/useLLMProxyClient';
import {useWbAgentClient} from '@wandb/weave/lib/WbAgent/useWbAgentClient';
import React, {useCallback, useMemo} from 'react';

import {toRuntimeMessage} from '../../agentChat/messageConversions';
import {WBAgentRuntime} from '../../agentChat/runtime/backend';
import {ClientAgentRuntime} from '../../agentChat/runtime/client';
import {OpenAIResponsesAdapter} from '../../agentChat/runtime/providers/openAI';
import {
  AgentChatStoreProvider,
  selectThreadBranch,
  useAgentChatStateRef,
} from '../../agentChat/store';
import {useAgentChatRuntime} from '../../agentChat/useAgentChatRuntime';
import {useNavContext} from '../NavContextProvider';
import {
  AgentChatAvailabilityContext,
  useComputeAgentChatAvailability,
} from './useAgentChatAvailability';

// Toggle: set to `false` to use the client-side LLM runtime instead.
const USE_BACKEND_RUNTIME = true;

/**
 * Top-level provider for the Agent Chat feature.
 *
 * When the feature is available (gate + org settings pass), mounts the chat
 * store and AUI runtime. The runtime gracefully handles missing entity/project
 * context (turns are no-ops until context is present).
 */
export function AgentChatProvider({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  const availability = useComputeAgentChatAvailability();

  return (
    <AgentChatAvailabilityContext.Provider value={availability}>
      {availability.isAvailable ? (
        <AgentChatStoreProvider>
          <AssistantRuntime>{children}</AssistantRuntime>
        </AgentChatStoreProvider>
      ) : (
        <>{children}</>
      )}
    </AgentChatAvailabilityContext.Provider>
  );
}

function AssistantRuntime({children}: {children: React.ReactNode}) {
  const {params} = useNavContext();
  const wbAgentClient = useWbAgentClient(params.entityName, params.projectName);
  const client = useLLMProxyClient(params.entityName, params.projectName);
  const agentChatStateRef = useAgentChatStateRef();

  const getParentNodeId = useCallback(
    (nodeId: string) => {
      const state = agentChatStateRef.current;
      const thread = state.threads[state.activeThreadId ?? ''];
      return thread?.messageTree.parentId[nodeId] ?? null;
    },
    [agentChatStateRef]
  );

  const getThreadMessages = useCallback(
    (threadId: string, upToNodeId: string | null) => {
      if (!upToNodeId) {
        return [];
      }

      return selectThreadBranch(
        agentChatStateRef.current,
        threadId,
        upToNodeId
      ).map(toRuntimeMessage);
    },
    [agentChatStateRef]
  );

  const agentRuntime = useMemo(() => {
    if (USE_BACKEND_RUNTIME && wbAgentClient) {
      return new WBAgentRuntime(wbAgentClient, getParentNodeId);
    }

    if (!client) {
      return null;
    }

    const adapter = new OpenAIResponsesAdapter();
    return new ClientAgentRuntime(client, adapter, getThreadMessages);
  }, [wbAgentClient, client, getParentNodeId, getThreadMessages]);

  const auiRuntime = useAgentChatRuntime(agentRuntime);
  const aui = useAui({threads: RuntimeAdapter(auiRuntime)});

  return <AuiProvider value={aui}>{children}</AuiProvider>;
}
